import { createSlice } from "@reduxjs/toolkit"
import {
  tenantGetIntegrationCategories,
  tenantGetIntegrationsCatalogTree,
  tenantGetAvailableIntegrations,
  tenantGetMyIntegrations,
  tenantGetMyIntegrationDetails,
  tenantConnectIntegration,
  tenantUpdateMyIntegration,
  tenantDisconnectMyIntegration,
  tenantDeleteMyIntegration,
  tenantRequestIntegrationStepUpView,
  tenantRequestIntegrationStepUpEdit,
  tenantRequestIntegrationStepUpDisconnect,
  tenantRequestIntegrationStepUpDelete,
  tenantVerifyIntegrationStepUp,
  tenantTestSmsIntegration,
} from "store/actions/tenantIntegrationAction"

const pickCatalogTreeArray = obj => {
  if (!obj) return null
  if (Array.isArray(obj)) return obj
  const keys = [
    "tree",
    "catalog",
    "categories",
    "parents",
    "items",
    "nodes",
    "roots",
    "groups",
    "list",
    "rows",
    "data",
  ]
  for (let i = 0; i < keys.length; i += 1) {
    const k = keys[i]
    if (Array.isArray(obj[k])) return obj[k]
  }
  return null
}

const initialState = {
  categoriesLoading: false,
  categories: [],

  catalogTreeLoading: false,
  catalogTree: [],

  availableLoading: false,
  available: [],
  availableTotalCount: 0,

  myLoading: false,
  my: [],
  myTotalCount: 0,
  myCurrentPage: 1,

  detailsLoading: false,
  details: null,

  actionLoading: false,
  error: null,

  updateOtpRequestLoading: false,
  updateOtpVerifyLoading: false,
  testSmsLoading: false,
}

const tenantIntegrationSlice = createSlice({
  name: "tenantIntegration",
  initialState,
  reducers: {
    clearTenantIntegrationDetails(state) {
      state.details = null
    },
  },
  extraReducers: builder => {
    builder
      .addCase(tenantGetIntegrationCategories.pending, state => {
        state.categoriesLoading = true
      })
      .addCase(tenantGetIntegrationCategories.fulfilled, (state, action) => {
        state.categoriesLoading = false
        const { status, data } = action.payload || {}
        if (status) {
          state.categories = Array.isArray(data) ? data : []
        }
      })
      .addCase(tenantGetIntegrationCategories.rejected, state => {
        state.categoriesLoading = false
      })

    builder
      .addCase(tenantGetIntegrationsCatalogTree.pending, state => {
        state.catalogTreeLoading = true
      })
      .addCase(tenantGetIntegrationsCatalogTree.fulfilled, (state, action) => {
        state.catalogTreeLoading = false
        const payload = action.payload || {}
        const { data } = payload
        const tree = pickCatalogTreeArray(data !== undefined ? data : payload)
        state.catalogTree = Array.isArray(tree) ? tree : []
      })
      .addCase(tenantGetIntegrationsCatalogTree.rejected, state => {
        state.catalogTreeLoading = false
        state.catalogTree = []
      })

    builder
      .addCase(tenantGetAvailableIntegrations.pending, state => {
        state.availableLoading = true
      })
      .addCase(tenantGetAvailableIntegrations.fulfilled, (state, action) => {
        state.availableLoading = false
        const { status, data, totalCount } = action.payload || {}
        if (status) {
          state.available = Array.isArray(data) ? data : []
          state.availableTotalCount = totalCount || 0
        }
      })
      .addCase(tenantGetAvailableIntegrations.rejected, state => {
        state.availableLoading = false
      })

    builder
      .addCase(tenantGetMyIntegrations.pending, state => {
        state.myLoading = true
      })
      .addCase(tenantGetMyIntegrations.fulfilled, (state, action) => {
        state.myLoading = false
        const { status, data, totalCount, currentPage } = action.payload || {}
        if (status) {
          state.my = Array.isArray(data) ? data : []
          state.myTotalCount = totalCount || 0
          state.myCurrentPage = currentPage || 1
        }
      })
      .addCase(tenantGetMyIntegrations.rejected, state => {
        state.myLoading = false
      })

    builder
      .addCase(tenantGetMyIntegrationDetails.pending, state => {
        state.detailsLoading = true
        state.details = null
      })
      .addCase(tenantGetMyIntegrationDetails.fulfilled, (state, action) => {
        state.detailsLoading = false
        const p = action.payload || {}
        const ok =
          p.status !== false &&
          (p.status === true || p.statusCode === 200 || p.data != null)
        if (ok) {
          state.details = p.data != null ? p.data : null
        }
      })
      .addCase(tenantGetMyIntegrationDetails.rejected, state => {
        state.detailsLoading = false
      })

    const actionThunks = [
      tenantConnectIntegration,
      tenantUpdateMyIntegration,
      tenantDisconnectMyIntegration,
      tenantDeleteMyIntegration,
    ]
    actionThunks.forEach(thunk => {
      builder
        .addCase(thunk.pending, state => {
          state.actionLoading = true
        })
        .addCase(thunk.fulfilled, state => {
          state.actionLoading = false
        })
        .addCase(thunk.rejected, state => {
          state.actionLoading = false
        })
    })

    builder
      .addCase(tenantRequestIntegrationStepUpView.pending, state => {
        state.updateOtpRequestLoading = true
      })
      .addCase(tenantRequestIntegrationStepUpView.fulfilled, state => {
        state.updateOtpRequestLoading = false
      })
      .addCase(tenantRequestIntegrationStepUpView.rejected, state => {
        state.updateOtpRequestLoading = false
      })
      .addCase(tenantRequestIntegrationStepUpEdit.pending, state => {
        state.updateOtpRequestLoading = true
      })
      .addCase(tenantRequestIntegrationStepUpEdit.fulfilled, state => {
        state.updateOtpRequestLoading = false
      })
      .addCase(tenantRequestIntegrationStepUpEdit.rejected, state => {
        state.updateOtpRequestLoading = false
      })
      .addCase(tenantRequestIntegrationStepUpDisconnect.pending, state => {
        state.updateOtpRequestLoading = true
      })
      .addCase(tenantRequestIntegrationStepUpDisconnect.fulfilled, state => {
        state.updateOtpRequestLoading = false
      })
      .addCase(tenantRequestIntegrationStepUpDisconnect.rejected, state => {
        state.updateOtpRequestLoading = false
      })
      .addCase(tenantRequestIntegrationStepUpDelete.pending, state => {
        state.updateOtpRequestLoading = true
      })
      .addCase(tenantRequestIntegrationStepUpDelete.fulfilled, state => {
        state.updateOtpRequestLoading = false
      })
      .addCase(tenantRequestIntegrationStepUpDelete.rejected, state => {
        state.updateOtpRequestLoading = false
      })
      .addCase(tenantVerifyIntegrationStepUp.pending, state => {
        state.updateOtpVerifyLoading = true
      })
      .addCase(tenantVerifyIntegrationStepUp.fulfilled, state => {
        state.updateOtpVerifyLoading = false
      })
      .addCase(tenantVerifyIntegrationStepUp.rejected, state => {
        state.updateOtpVerifyLoading = false
      })

    builder
      .addCase(tenantTestSmsIntegration.pending, state => {
        state.testSmsLoading = true
      })
      .addCase(tenantTestSmsIntegration.fulfilled, state => {
        state.testSmsLoading = false
      })
      .addCase(tenantTestSmsIntegration.rejected, state => {
        state.testSmsLoading = false
      })
  },
})

export const { clearTenantIntegrationDetails } = tenantIntegrationSlice.actions
export default tenantIntegrationSlice.reducer
